import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatPhone } from "@/lib/validators";
import { DocumentInput } from "@/components/DocumentInput";
import { validateDocument } from "@/utils/document";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";
import { authLocationPath } from "@/lib/auth-navigation";

interface BecomeProducerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = "auth" | "producer-data";

export function BecomeProducerModal({ open, onOpenChange }: BecomeProducerModalProps) {
  const { t } = useTranslation("producerLanding");
  const { user, profile, role, refetchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>(user ? "producer-data" : "auth");
  const [authTab, setAuthTab] = useState<"login" | "register">("register");
  const [showPassword, setShowPassword] = useState(false);
  const [isUltraCompact, setIsUltraCompact] = useState(false);

  // Auth fields
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");

  // Producer fields
  const [documentNumber, setDocumentNumber] = useState("");
  const [phone, setPhone] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const updateViewportMode = () => {
      const viewportHeight = window.innerHeight;
      setIsUltraCompact(viewportHeight <= 640);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  // Reset step when modal opens
  useEffect(() => {
    if (open) {
      if (!user && window.matchMedia("(min-width: 1024px)").matches) {
        const params = new URLSearchParams({ next: "/produtores?cadastro=produtor", back: authLocationPath(location), view: "register" });
        onOpenChange(false);
        navigate(`/entrar?${params}`);
        return;
      }
      if (user && role === "producer") {
        sessionStorage.removeItem("become_producer_flow");
        onOpenChange(false);
        navigate("/produtor/eventos/novo");
        return;
      }
      setStep(user ? "producer-data" : "auth");
      setDocumentNumber(profile?.document_number || (profile as typeof profile & { cnpj?: string })?.cnpj || "");
      setPhone(profile?.phone || "");
    } else {
      // Clean up session storage when modal closes
      sessionStorage.removeItem("become_producer_flow");
    }
  }, [open, user, role, navigate, onOpenChange, profile, location]);

  // Handle OAuth callback completing the flow
  useEffect(() => {
    if (open && user && step === "auth" && sessionStorage.getItem("become_producer_flow")) {
      setStep("producer-data");
      setDocumentNumber(profile?.document_number || (profile as typeof profile & { cnpj?: string })?.cnpj || "");
      setPhone(profile?.phone || "");
    }
  }, [user, open, step, profile]);

  // --- Auth handlers ---
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });
    setLoading(false);
    if (error) {
      console.error("BecomeProducer login error:", error);
      toast({ title: t("becomeProducerModal.toasts.loginError"), variant: "destructive" });
    }
    // onAuthStateChange in AuthContext will update `user`, triggering auto-advance
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (regPassword !== regConfirm) {
      toast({ title: t("becomeProducerModal.toasts.passwordMismatch"), variant: "destructive" });
      return;
    }
    if (regPassword.length < 6) {
      toast({ title: t("becomeProducerModal.toasts.passwordTooShort"), variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email: regEmail,
      password: regPassword,
      options: {
        data: { full_name: regName },
      },
    });
    setLoading(false);
    if (error) {
      console.error("BecomeProducer register error:", error);
      toast({ title: t("becomeProducerModal.toasts.registerError"), variant: "destructive" });
      return;
    }
  };

  // --- Producer handler ---
  const handleProducerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const documentValidation = validateDocument(documentNumber);
    if (!documentValidation.valid || !documentValidation.type) {
      toast({ title: documentValidation.error || t("becomeProducerModal.toasts.invalidDocument"), variant: "destructive" });
      return;
    }
    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      toast({ title: t("becomeProducerModal.toasts.invalidPhone"), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      console.info("[become-producer] submit start: " + JSON.stringify({
        userId: user?.id ?? null,
        role,
        profileLoaded: !!profile,
        hasDocument: Boolean(documentNumber),
        hasPhone: Boolean(phone),
      }));
      const { data: { session } } = await supabase.auth.getSession();
      console.info("[become-producer] session snapshot: " + JSON.stringify({
        hasSession: !!session,
        userId: session?.user?.id ?? null,
        expiresAt: session?.expires_at ?? null,
        hasAccessToken: !!session?.access_token,
        accessTokenPrefix: session?.access_token ? session.access_token.slice(0, 12) : null,
      }));
      if (!session?.access_token) {
        console.error("[become-producer] missing access token before function invoke");
        toast({ title: t("becomeProducerModal.toasts.invalidSession"), variant: "destructive" });
        return;
      }
      const result = await supabase.functions.invoke("become-producer", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { document_number: documentNumber, document_type: documentValidation.type, phone: cleanPhone },
      });
      console.info("[become-producer] function result: " + JSON.stringify({
        hasError: !!result.error,
        data: result.data ?? null,
        errorMessage: result.error?.message ?? null,
      }));
      if (result.error) throw result.error;
      const data = result.data as { alreadyProducer?: boolean } | null;
      if (data?.alreadyProducer) {
        toast({ title: t("becomeProducerModal.toasts.alreadyProducer") });
      } else {
        toast({ title: t("becomeProducerModal.toasts.producerActivated") });
      }
      await refetchRole();
      onOpenChange(false);
      navigate("/produtor/eventos/novo");
    } catch (error) {
      console.error("[become-producer] submit failed", error);
      toast({ title: t("becomeProducerModal.toasts.activationError"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[420px] p-0 overflow-hidden border-none bg-transparent shadow-2xl"
      >
        <DialogTitle className="sr-only">{t("becomeProducerModal.dialogTitle")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("becomeProducerModal.dialogDescription")}
        </DialogDescription>
        
        <div className="bg-card border border-border sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col">
          {/* Brand Header */}
          <div className="h-2 w-full bg-gradient-to-r from-primary via-accent to-primary" />
          
          <div className={`${isUltraCompact ? "p-5 space-y-4" : "p-8 space-y-6"}`}>
            {/* ============ STEP 1: AUTH ============ */}
            {step === "auth" && (
              <>
                <div className="space-y-2">
                  <h2
                    className={`font-display font-bold text-foreground leading-tight ${
                      isUltraCompact ? "text-xl" : "text-2xl"
                    }`}
                  >
                    {authTab === "login" ? t("becomeProducerModal.auth.loginTitle") : t("becomeProducerModal.auth.registerTitle")}
                  </h2>
                  <p className={`${isUltraCompact ? "text-xs" : "text-sm"} text-muted-foreground leading-relaxed`}>
                    {authTab === "login"
                      ? t("becomeProducerModal.auth.loginSubtitle")
                      : t("becomeProducerModal.auth.registerSubtitle")}
                  </p>
                </div>

                {/* Tab toggle */}
                <div className="flex p-1 bg-secondary/50 rounded-lg border border-border/50">
                  <button
                    type="button"
                    onClick={() => setAuthTab("login")}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      authTab === "login"
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t("becomeProducerModal.auth.tabs.login")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthTab("register")}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      authTab === "register"
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t("becomeProducerModal.auth.tabs.register")}
                  </button>
                </div>

                {authTab === "login" ? (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1">{t("becomeProducerModal.auth.emailLabel")}</Label>
                      <Input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder={t("becomeProducerModal.auth.emailPlaceholder")}
                        required
                        className="h-11 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1">{t("becomeProducerModal.auth.passwordLabel")}</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder={t("becomeProducerModal.auth.passwordPlaceholderDots")}
                          required
                          className="h-11 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl pr-10"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 gap-2 rounded-xl font-semibold shadow-lg shadow-primary/10 transition-all hover:shadow-primary/20 active:scale-[0.98]"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                      {loading ? t("becomeProducerModal.auth.loginSubmitLoading") : t("becomeProducerModal.auth.loginSubmit")}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1">{t("becomeProducerModal.auth.nameLabel")}</Label>
                      <Input
                        value={regName}
                        onChange={(e) => setRegName(e.target.value)}
                        placeholder={t("becomeProducerModal.auth.namePlaceholder")}
                        required
                        className="h-11 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1">{t("becomeProducerModal.auth.emailLabel")}</Label>
                      <Input
                        type="email"
                        value={regEmail}
                        onChange={(e) => setRegEmail(e.target.value)}
                        placeholder={t("becomeProducerModal.auth.emailPlaceholder")}
                        required
                        className="h-11 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1">{t("becomeProducerModal.auth.passwordLabel")}</Label>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          placeholder={t("becomeProducerModal.auth.passwordPlaceholder")}
                          required
                          className="h-11 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1 text-nowrap truncate">{t("becomeProducerModal.auth.confirmLabel")}</Label>
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={regConfirm}
                          onChange={(e) => setRegConfirm(e.target.value)}
                          placeholder={t("becomeProducerModal.auth.confirmPlaceholder")}
                          required
                          className="h-11 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 gap-2 rounded-xl font-semibold shadow-lg shadow-primary/10 transition-all hover:shadow-primary/20 active:scale-[0.98]"
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                      {loading ? t("becomeProducerModal.auth.registerSubmitLoading") : t("becomeProducerModal.auth.registerSubmit")}
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center px-4 leading-relaxed">
                      {t("becomeProducerModal.auth.termsPrefix")}{" "}
                      <Link to="/termos-de-uso/produtor" className="text-primary hover:underline font-medium">{t("becomeProducerModal.auth.termsLink")}</Link>
                      {" "}{t("becomeProducerModal.auth.termsMiddle")}{" "}
                      <Link to="/politica-de-privacidade" className="text-primary hover:underline font-medium">{t("becomeProducerModal.auth.privacyLink")}</Link>.
                    </p>
                  </form>
                )}

                {/* Footer switch */}
                <p className={`text-center ${isUltraCompact ? "text-xs" : "text-sm"} text-muted-foreground`}>
                  {authTab === "login" ? (
                    <>{t("becomeProducerModal.auth.noAccountPrefix")}{" "}<button type="button" onClick={() => setAuthTab("register")} className="text-primary font-bold hover:underline transition-all underline-offset-2">{t("becomeProducerModal.auth.createFreeAccountButton")}</button></>
                  ) : (
                    <>{t("becomeProducerModal.auth.hasAccountPrefix")}{" "}<button type="button" onClick={() => setAuthTab("login")} className="text-primary font-bold hover:underline transition-all underline-offset-2">{t("becomeProducerModal.auth.loginButton")}</button></>
                  )}
                </p>
              </>
            )}

            {/* ============ STEP 2: PRODUCER DATA ============ */}
            {step === "producer-data" && (
              <>
                <div className="space-y-2 text-center pb-2">
                  <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary/10 text-primary mb-2">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                  <h2 className="font-display font-bold text-foreground text-2xl leading-tight">
                    {t("becomeProducerModal.producerData.title")}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed px-4">
                    {t("becomeProducerModal.producerData.subtitle")}
                  </p>
                </div>

                <form onSubmit={handleProducerSubmit} className="space-y-5 pt-2">
                  <div className="space-y-1.5">
                    <DocumentInput
                      label={t("becomeProducerModal.producerData.documentLabel")}
                      value={documentNumber}
                      onChange={setDocumentNumber}
                      disabled={loading}
                      className="h-12 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider ml-1">{t("becomeProducerModal.producerData.phoneLabel")}</Label>
                    <Input
                      id="phone"
                      placeholder={t("becomeProducerModal.producerData.phonePlaceholder")}
                      value={phone}
                      onChange={(e) => setPhone(formatPhone(e.target.value))}
                      maxLength={15}
                      required
                      className="h-12 bg-secondary/30 border-border/60 focus:border-primary/50 transition-all rounded-xl"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl font-bold shadow-lg shadow-primary/10 transition-all hover:shadow-primary/20 active:scale-[0.98] mt-2 group"
                    disabled={loading}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t("becomeProducerModal.producerData.submitLoading")}</>
                    ) : (
                      <>{t("becomeProducerModal.producerData.submit")} <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
